import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Text,
  Button,
  Flex,
  VStack,
  HStack,
  useToast,
  Image,
  Icon,
  Spinner,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  useDisclosure,
  Alert,
  AlertIcon,
  Badge,
  Divider,
  useColorModeValue,
  useBreakpointValue
} from '@chakra-ui/react';
import { Link, useNavigate } from 'react-router-dom';
import { FaWallet, FaExclamationTriangle, FaPlus } from 'react-icons/fa';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../hooks/useAuth';
import { useWalletContext } from '../../context/WalletContext';
import axiosInstance from '../../services/axios';
import AddFundsModal from './AddFundsModal';
import { getErrorMessage } from '../../utils/errorUtils';

const WalletCheckout = () => {
  const { cartItems, clearCart } = useCart();
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const { walletSummary, refreshWallet, checkSpendingAbility } = useWalletContext();
  
  // Responsive values
  const cardDirection = useBreakpointValue({ base: 'column', md: 'row' });
  const cardSpacing = useBreakpointValue({ base: 4, md: 8 });
  const imageSize = useBreakpointValue({ base: '3rem', md: '4rem' });
  
  // Theme colors
  const bgColor = 'dark.500';
  const summaryBgColor = 'secondary.100';
  const textColor = 'white';
  const mutedTextColor = 'gray.400';
  
  // State
  const [loading, setLoading] = useState(false);
  const [spendingCheck, setSpendingCheck] = useState(null);
  
  // Add funds modal
  const { isOpen: isAddFundsOpen, onOpen: onAddFundsOpen, onClose: onAddFundsClose } = useDisclosure();

  // Calculate totals
  const calculateSubtotal = useCallback(() => {
    return cartItems.reduce((total, item) => total + (item.price_per_share * item.quantity), 0);
  }, [cartItems]);

  const [subtotal, setSubtotal] = useState(calculateSubtotal());
  const [finalTotal, setFinalTotal] = useState(calculateSubtotal());

  // Update calculations when cart items change
  useEffect(() => {
    const newSubtotal = calculateSubtotal();
    setSubtotal(newSubtotal);
    setFinalTotal(newSubtotal);
  }, [calculateSubtotal]);

  // Check spending ability when wallet or total changes
  useEffect(() => {
    const checkAbility = async () => {
      if (finalTotal > 0 && walletSummary) {
        try {
          const result = await checkSpendingAbility(finalTotal);
          setSpendingCheck(result);
        } catch (error) {
          console.error('Error checking spending ability:', error);
        }
      }
    };
    
    checkAbility();
  }, [finalTotal, walletSummary, checkSpendingAbility]);


  const handleWalletPurchase = async () => {
    if (!spendingCheck?.can_spend) {
      onAddFundsOpen();
      return;
    }

    setLoading(true);
    try {
      const totalShares = cartItems.reduce((sum, item) => sum + item.quantity, 0);
      
      // Make sure each cart item has a seller_id
      const cartItemsWithSeller = cartItems.map(item => ({
        ...item,
        seller_id: item.seller_id || item.publisher.id
      }));
      
      // Complete order with wallet payment
      await axiosInstance.post('/music/complete_wallet_order/', {
        cart_items: cartItemsWithSeller,
        total_price: finalTotal,
        payment_method: 'wallet',
        buyer_id: user.user_id,
        total_shares: totalShares,
        transaction_type: 'shares',
      });
      
      clearCart();
      await refreshWallet(); // Refresh wallet balance
      
      toast({
        title: 'Order placed successfully.',
        description: 'Your purchase has been completed using your wallet.',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });
      
      navigate('/order-confirmation');
    } catch (error) {
      toast({
        title: 'Error',
        description: getErrorMessage(error, 'Failed to complete the order. Please try again.'),
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleAddFundsSuccess = () => {
    onAddFundsClose();
    refreshWallet();
    
    toast({
      title: 'Funds added successfully',
      description: 'You can now complete your purchase.',
      status: 'success',
      duration: 3000,
      isClosable: true,
    });
  };

  if (!walletSummary) {
    return (
      <Box p={4} bg={bgColor} borderRadius="md">
        <Flex justify="center" align="center" minH="200px">
          <Spinner size="lg" color="primary.500" />
          <Text ml={4} color={textColor}>Loading wallet...</Text>
        </Flex>
      </Box>
    );
  }

  return (
    <Box
      p={4}
      display="flex"
      flexDirection={cardDirection}
      justifyContent="space-around"
      alignItems="flex-start"
      gap={cardSpacing}
    >
      {/* Cart Summary */}
      <Box maxW="500px" flex={2} p={4} borderRadius="md" mb={{ base: 4, md: 0 }} bg={bgColor}>
        <Text fontSize="2xl" mb={4} color={textColor} fontWeight="bold">Checkout</Text>
        
        {cartItems.map((item, index) => (
          <Flex key={index} align="center" gap={4} justify="space-between" p={3}>
            <Image
              src={item.image_url}
              alt={item.track_name}
              borderRadius="lg"
              width={imageSize}
              fallbackSrc="/placeholder-music.png"
            />
            <Text 
              as={Link} 
              to={`/track/${item.track_id}`} 
              w="50%" 
              fontSize="sm" 
              noOfLines={1} 
              color={textColor}
              _hover={{ color: "primary.300" }}
            >
              {item.track_name}
            </Text>
            <Text color={textColor} fontWeight="medium">
              ${item.price_per_share} x {item.quantity}
            </Text>
          </Flex>
        ))}


        {/* Price Summary */}
        <VStack spacing={2} mt={4}>
          <Flex w="100%" justifyContent="space-between" alignItems="center" p={4} bg={summaryBgColor} borderRadius={3}>
            <Text fontSize="lg" color={textColor}>Subtotal:</Text>
            <Text fontSize="lg" fontWeight="bold" color={textColor}>${subtotal.toFixed(2)}</Text>
          </Flex>
          
          <Flex w="100%" justifyContent="space-between" alignItems="center" p={4} bg={summaryBgColor} borderRadius={3}>
            <Text fontSize="lg" color={textColor}>Processing Fee:</Text>
            <Text fontSize="lg" fontWeight="bold" color="green.400">$0.00</Text>
          </Flex>
          
          <Flex w="100%" justifyContent="space-between" alignItems="center" p={4} bg="dark.500" borderRadius={3}>
            <Text fontSize="lg" fontWeight="bold" color="white">Total:</Text>
            <Text fontSize="lg" fontWeight="bold" color="white">${finalTotal.toFixed(2)}</Text>
          </Flex>
        </VStack>

      </Box>

      {/* Wallet Payment Section */}
      <Box flex={1} maxW="500px" bg={bgColor} p={6} borderRadius="md">
        <VStack spacing={6} align="stretch">
          {/* Wallet Balance Display */}
          <Box>
            <HStack justify="space-between" mb={4}>
              <HStack>
                <Icon as={FaWallet} color="primary.400" boxSize={6} />
                <Text fontSize="xl" fontWeight="bold" color={textColor}>Your Wallet</Text>
              </HStack>
              {walletSummary.pending_earnings > 0 && (
                <Badge colorScheme="yellow" variant="subtle">
                  {walletSummary.pending_earnings_count} pending
                </Badge>
              )}
            </HStack>
            
            <VStack spacing={3} align="stretch">
              <Flex justify="space-between" align="center" p={4} bg={summaryBgColor} borderRadius="md">
                <Text color={textColor}>Available Balance:</Text>
                <Text fontSize="xl" fontWeight="bold" color="primary.400">
                  ${walletSummary.available_balance.toFixed(2)}
                </Text>
              </Flex>
              
              {walletSummary.pending_earnings > 0 && (
                <Flex justify="space-between" align="center" p={3} bg="yellow.50" borderRadius="md">
                  <Text color="yellow.800" fontSize="sm">Pending Earnings:</Text>
                  <Text fontSize="sm" fontWeight="medium" color="yellow.700">
                    ${walletSummary.pending_earnings.toFixed(2)}
                  </Text>
                </Flex>
              )}
            </VStack>
          </Box>

          <Divider borderColor="gray.600" />

          {/* Purchase Section */}
          <Box>
            <Text fontSize="lg" fontWeight="bold" mb={4} color={textColor}>
              Complete Purchase
            </Text>
            
            {spendingCheck?.can_spend ? (
              <VStack spacing={4}>
                <Alert status="success" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm">Sufficient funds available for purchase</Text>
                </Alert>
                
                <Button
                  w="100%"
                  size="lg"
                  colorScheme="green"
                  onClick={handleWalletPurchase}
                  isLoading={loading}
                  loadingText="Processing..."
                  leftIcon={<FaWallet />}
                >
                  Pay ${finalTotal.toFixed(2)} with Wallet
                </Button>
              </VStack>
            ) : (
              <VStack spacing={4}>
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <Text fontSize="sm" fontWeight="medium">Insufficient funds</Text>
                    <Text fontSize="xs" color="orange.600">
                      Need ${spendingCheck?.shortfall?.toFixed(2) || '0.00'} more to complete this purchase
                    </Text>
                  </VStack>
                </Alert>
                
                <Button
                  w="100%"
                  size="lg"
                  colorScheme="primary"
                  onClick={onAddFundsOpen}
                  leftIcon={<FaPlus />}
                >
                  Add ${spendingCheck?.shortfall?.toFixed(2) || finalTotal.toFixed(2)} to Wallet
                </Button>
                
                {walletSummary.pending_earnings > 0 && (
                  <Text fontSize="xs" color={mutedTextColor} textAlign="center">
                    ${walletSummary.pending_earnings.toFixed(2)} in earnings will be available after business day clearance
                  </Text>
                )}
              </VStack>
            )}
          </Box>
        </VStack>
      </Box>

      {/* Add Funds Modal */}
      <AddFundsModal
        isOpen={isAddFundsOpen}
        onClose={onAddFundsClose}
        onSuccess={handleAddFundsSuccess}
        minimumAmount={spendingCheck?.shortfall || 0}
        suggestedAmount={Math.ceil((spendingCheck?.shortfall || finalTotal) / 25) * 25}
      />
    </Box>
  );
};

export default WalletCheckout;